/**
 * WizardChat - scrollable message list for wizard conversations.
 *
 * User messages align right (teal), assistant messages align left (subtle bg).
 * During streaming, displays the partial text with a blinking cursor.
 */

import { useEffect, useRef } from 'react'
import { Bot, User } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import type { WizardMessage } from '@/admin/hooks/use-wizard'
import { cn } from '@/shared/lib/utils'

interface WizardChatProps {
  messages: WizardMessage[]
  isStreaming: boolean
  streamingText: string
}

export function WizardChat({ messages, isStreaming, streamingText }: WizardChatProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, streamingText])

  return (
    <div className="min-h-[200px] flex-1 overflow-y-auto px-6 py-6 space-y-4">
      {messages.length === 0 && !isStreaming && (
        <div className="flex flex-col items-center gap-3 pt-10 opacity-50">
          <Bot className="h-8 w-8 text-[var(--ink-3)]" />
          <p className="text-[13px] text-[var(--ink-3)]">
            Upload a spreadsheet to get started, or describe what you'd like to set up.
          </p>
        </div>
      )}
      {messages.map((msg, i) => (
        <MessageBubble key={i} message={msg} />
      ))}
      {isStreaming && streamingText && (
        <MessageBubble
          message={{ role: 'assistant', content: streamingText, timestamp: '' }}
          isStreaming
        />
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function MessageBubble({
  message,
  isStreaming = false,
}: {
  message: WizardMessage
  isStreaming?: boolean
}) {
  const isUser = message.role === 'user'

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          isUser
            ? 'bg-[var(--green)] text-[var(--bg)]'
            : 'bg-[var(--bg-subtle)] text-[var(--ink-2)]'
        )}
      >
        {isUser ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
      </div>
      {/* Bubble */}
      <div
        className={cn(
          "max-w-[75%] rounded-[8px] border-[0.5px] border-[var(--rule)] px-4 py-3 text-[13px] leading-relaxed text-[var(--ink)]",
          isUser
            ? 'whitespace-pre-wrap bg-[var(--green-bg-muted)] text-right'
            : 'wizard-prose bg-[var(--bg-subtle)]'
        )}
      >
        {isUser ? (
          message.content
        ) : (
          <ReactMarkdown>{message.content}</ReactMarkdown>
        )}
        {isStreaming && (
          <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse align-text-bottom bg-[var(--green)]" />
        )}
      </div>
    </div>
  );
}
